import { useExtraConfigContext } from '@gov-nx/core/app';
import { type ServiceCode } from '@gov-nx/module/service';
import { usePoIndividualDataLoad } from '../../Person/usePoIndividualDataLoad';
import type { RezaSwitcherType } from './rezaSwitcher.types';
import { useBoolean } from '@gov-nx/utils/common';
import { getRezaPersonIdentity, getTableKeyValues } from './utils';
import { useRezaSwitcherLocale } from './useRezaSwitcherLocale';
import { useCmsServiceQuery } from '@gov-nx/core/service';
import { useRezaSwitcherStore } from '@gov-nx/store';
import { useRosBusinessPersonQuery } from '../../Person/useRosBusinessPersonQuery';
import { useRezaBlockingMutation } from './useRezaBlockingMutation';
import { useRezaChangeOptions } from './useRezaChangeOptions';

interface useRezaSwitcherProps {
	code: ServiceCode;
}

export const useRezaSwitcher = ({
	code,
}: useRezaSwitcherProps): RezaSwitcherType => {
	const { t } = useRezaSwitcherLocale();
	const { extraConfig } = useExtraConfigContext();
	const isRezaChangeModalOpen = useBoolean();
	const { activeSwitcherReza, setActiveSwitcherReza } = useRezaSwitcherStore();
	const { blockingMutationKey, isBlocked } = useRezaBlockingMutation(code);

	const cmsServiceQuery = useCmsServiceQuery({ code });
	const permissions = cmsServiceQuery.data?.rezaPermissions ?? {
		implicitAllowed: false,
		implicitFilter: [],
		explicitAllowed: false,
		explicitFilter: [],
	};
	const optionsData = useRezaChangeOptions({ permissions });
	const { hasRepresentationOptions, isRezaListLoading, rezaList } = optionsData;
	const person = usePoIndividualDataLoad();
	const isRezaButtonVisible =
		hasRepresentationOptions || rezaList.isError || activeSwitcherReza !== null;
	const rezaPravnickaOsoba =
		activeSwitcherReza?.zastupujiciOsoba?.pravnickaOsoba;
	const rosBusinessPersonQuery = useRosBusinessPersonQuery({
		enabled:
			!!rezaPravnickaOsoba?.ico && !rezaPravnickaOsoba?.adresa?.adresaText,
	});

	const podnikateleSeznam = rosBusinessPersonQuery.data?.seznam ?? [];
	const personIdentity = getRezaPersonIdentity(
		activeSwitcherReza,
		person.individualPerson,
		podnikateleSeznam
	);
	const personKeyValues = getTableKeyValues(
		activeSwitcherReza,
		person.individualPerson,
		podnikateleSeznam,
		t
	);

	return {
		activeSwitcherReza,
		isRezaSwitcherEnabled: extraConfig.components.isRezaSwitcherEnabled,
		permissions,
		description: t('table.description'),
		blockingMutationKey,
		personIdentity,
		rezaPersonTableProps: {
			isPersonLoading: person.isLoading,
			isPersonError: person.isError,
			keyValues: personKeyValues,
			errorBanner: {
				code,
				headline: t('table.error-banner.title'),
				text: t('table.error-banner.text'),
			},
		},
		rezaChangeModalProps: {
			isOpen: isRezaChangeModalOpen.value,
			title: t('modal.title.change-reza'),
			activeSwitcherReza,
			setActiveSwitcherReza,
			onClose: isRezaChangeModalOpen.setFalse,
			optionsData,
		},
		rezaButtonProps: {
			onModalOpen: isRezaChangeModalOpen.setTrue,
			text: t('button.change-reza'),
			isRezaButtonDisabled: isBlocked,
			isVisible: isRezaButtonVisible,
			isLoading: isRezaListLoading,
		},
	};
};
